import React, { useState } from 'react';
import { Badge, Dropdown, ListGroup, Button } from 'react-bootstrap';
import { Bell, CheckCircle, Truck, CheckCheck } from 'lucide-react';
import { useSocket } from '../context/SocketContext';
import { useCurrency } from '../context/currencyContext'; // Import currency context

const NotificationBell = () => {
  const { notifications, unreadCount, setNotifications, markAsRead } = useSocket();
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const { formatAmount } = useCurrency(); // Get formatAmount from currency context

  const markAsReadHandler = async (notificationId) => {
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      
      // Call API to mark as read in database
      await fetch(`https://hms.backend.hiragroupcloud.com/api/notifications/${notificationId}/read`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Update local state
      markAsRead(notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
      await fetch('https://hms.backend.hiragroupcloud.com/api/notifications/read-all', {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'outsource_request':
        return <Truck size={16} className="text-primary" />;
      case 'outsource_accepted':
        return <CheckCircle size={16} className="text-success" />;
      default:
        return <Bell size={16} className="text-secondary" />;
    }
  };

  const formatTime = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diff = now - notifDate;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return notifDate.toLocaleDateString();
  };

  return (
    <Dropdown 
      show={showDropdown} 
      onToggle={setShowDropdown}
      align="end"
    >
     <Dropdown.Toggle
        as="div"
        variant={null}
        className="notification-bell-toggle"
        style={{
          cursor: 'pointer',
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          border: '1px solid #f4f4f4',
          borderRadius: '4px',
          color: '#004aad',
          fontSize: 'calc(20px * var(--scale, 1))'
        }}
>
<Bell size="1em" color="#004aad" />
        {/* Show only red dot, no count number */}
        {unreadCount > 0 && (
<span
            style={{
              position: 'absolute',
              top: '2px',
              right: '2px',
              width: 'calc(10px * var(--scale, 1))',
              height: 'calc(10px * var(--scale, 1))',
              backgroundColor: '#dc3545',
              borderRadius: '50%',
              border: '2px solid white',
              animation: 'pulse 1.5s infinite'
            }}
          />
        )}
</Dropdown.Toggle>

      <Dropdown.Menu 
        style={{ 
          width: '380px', 
          maxHeight: '500px', 
          overflow: 'auto',
          padding: '0'
        }}
      >
        <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
          <h6 className="mb-0 fw-bold">
            Notifications
          </h6>
          {unreadCount > 0 && (
            <Button 
              variant="link" 
              size="sm" 
              onClick={markAllAsRead}
              disabled={loading}
              style={{ textDecoration: 'none' }}
            >
              <CheckCheck size={16} /> Mark all read
            </Button>
          )}
        </div>

        <ListGroup variant="flush">
          {notifications.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <Bell size={32} className="mb-2" />
              <p>No notifications yet</p>
            </div>
          ) : (
            notifications.map((notification) => (
              <ListGroup.Item 
                key={notification._id}
                className={`notification-item ${!notification.read ? 'bg-light' : ''}`}
                style={{ 
                  cursor: 'pointer',
                  borderLeft: !notification.read ? '3px solid #0d47a1' : 'none'
                }}
                onClick={() => {
                  if (!notification.read) {
                    markAsReadHandler(notification._id);
                  }
                }}
              >
                <div className="d-flex">
                  <div className="me-3">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start">
                      <strong style={{ fontSize: '14px' }}>{notification.title}</strong>
                      <small className="text-muted" style={{ fontSize: '11px' }}>
                        {formatTime(notification.createdAt)}
                      </small>
                    </div>
                    <p className="mb-1" style={{ fontSize: '13px' }}>
                      {notification.message}
                    </p>
                    {notification.data && (
                      <div style={{ fontSize: '12px' }}>
                        {notification.data.caseNumber && (
                          <span className="badge bg-info me-1">
                            Case #{notification.data.caseNumber}
                          </span>
                        )}
                        {notification.data.procedureName && (
                          <span className="badge bg-secondary">
                            {notification.data.procedureName}
                          </span>
                        )}
                        {notification.data.price && (
                          <span className="badge bg-success ms-1">
                            {formatAmount(notification.data.price)} {/* Using formatAmount from currency context */}
                          </span>
                        )}
                      </div>
                    )}
                    <div style={{ fontSize: '11px' }} className="text-muted mt-1">
                      From: {notification.sender?.labName || notification.sender || 'Unknown'}
                    </div>
                  </div>
                  {!notification.read && (
                    <div className="ms-2">
                      <span 
                        style={{ 
                          width: '8px', 
                          height: '8px', 
                          backgroundColor: '#0d47a1',
                          borderRadius: '50%',
                          display: 'inline-block'
                        }}
                      />
                    </div>
                  )}
                </div>
              </ListGroup.Item>
            ))
          )}
        </ListGroup>
      </Dropdown.Menu>
      
      <style>
        {`
          @keyframes pulse {
            0% {
              transform: scale(0.95);
              opacity: 0.7;
            }
            50% {
              transform: scale(1.2);
              opacity: 1;
            }
            100% {
              transform: scale(0.95);
              opacity: 0.7;
            }
          }
          
          .notification-item {
            transition: all 0.2s ease;
          }
          
          .notification-item:hover {
            background-color: #f8f9fa !important;
            transform: translateX(2px);
          }
        `}
      </style>
    </Dropdown>
  );
};

export default NotificationBell;